/**
 * @description 다양한 타입의 값을 로그로 표시하기 위한 도구 함수들로, console / trace plugin 에서 사용됩니다.
 * @see https://github.com/Tencent/vConsole/blob/dev/src/lib/tool.ts
 */

import {isString, isNumber, isBoolean, isNull, isUndefined, isFunction, isArray} from '@shared/helpers/lang'

import type {LogValueEntry, LogValueNode, LogValueType} from '../type'

/**
 * 값의 타입 판별
 */
const isBigInt = (value: unknown): value is bigint => typeof value === 'bigint'
const isSymbol = (value: unknown): value is symbol => typeof value === 'symbol'
const isObject = (value: unknown): value is Record<string, unknown> =>
    Object.prototype.toString.call(value) === '[object Object]' ||
    (!isNumber(value) &&
        !isBigInt(value) &&
        !isString(value) &&
        !isBoolean(value) &&
        !isArray(value) &&
        !isNull(value) &&
        !isFunction(value) &&
        !isUndefined(value) &&
        !isSymbol(value))

const isElement = (value: unknown): value is Element =>
    typeof HTMLElement === 'object'
        ? value instanceof HTMLElement
        : value != null &&
          typeof value === 'object' &&
          (value as Element).nodeType === 1 &&
          typeof (value as Element).nodeName === 'string'

const isMap = (value: unknown): value is Map<unknown, unknown> =>
    Object.prototype.toString.call(value) === '[object Map]'

const isSet = (value: unknown): value is Set<unknown> => Object.prototype.toString.call(value) === '[object Set]'

/**
 * 로그 값 타입 반환
 */
const getLogValueType = (value: unknown): LogValueType => {
    if (isNull(value)) {
        return 'null'
    }
    if (isUndefined(value)) {
        return 'undefined'
    }
    if (isBoolean(value)) {
        return 'boolean'
    }
    if (isNumber(value)) {
        return 'number'
    }
    if (isBigInt(value)) {
        return 'bigint'
    }
    if (isString(value)) {
        return 'string'
    }
    if (isSymbol(value)) {
        return 'symbol'
    }
    if (isFunction(value)) {
        return 'function'
    }
    if (isElement(value)) {
        return 'element'
    }
    if (isMap(value)) {
        return 'map'
    }
    if (isSet(value)) {
        return 'set'
    }
    if (isArray(value)) {
        return 'array'
    }
    return 'object'
}

/**
 * 객체 이름 추출
 */
const objNamePattern = /(function|class) ([^ {}()]{1,})[\s(]/
const getObjectName = (obj: unknown): string => {
    if (obj === null || obj === undefined) {
        return ''
    }
    const result = objNamePattern.exec((obj as object).constructor?.toString() ?? '')
    return result != null && result.length > 1 && result[2] != null ? result[2] : ''
}

/**
 * 순환 참조 감지
 */
const createCircularFinder = () => {
    const seen = new WeakSet()
    return (value: unknown): boolean => {
        if (typeof value === 'object' && value !== null) {
            if (seen.has(value)) {
                return true
            }
            seen.add(value)
        }
        return false
    }
}

/**
 * 미리보기 텍스트 생성
 */
const MAX_PREVIEW_LEN = 36

const getPreviewText = (value: unknown, seen = new WeakSet()): string => {
    if (isNull(value)) {
        return 'null'
    }
    if (isUndefined(value)) {
        return 'undefined'
    }
    if (isBoolean(value)) {
        return String(value)
    }
    if (isNumber(value)) {
        return String(value)
    }
    if (isBigInt(value)) {
        return String(value) + 'n'
    }
    if (isSymbol(value)) {
        return value.toString()
    }
    if (isFunction(value)) {
        return `${value.name || 'function'}()`
    }
    if (isString(value)) {
        return `"${value.replace(/\n/g, '\\n').replace(/\t/g, '\\t')}"`
    }
    if (isElement(value)) {
        return `<${(value as Element).nodeName.toLowerCase()} />`
    }

    // 순환 참조 감지
    if (typeof value === 'object' && value !== null) {
        if (seen.has(value)) {
            return isArray(value) ? '(Circular Array)' : `(Circular ${getObjectName(value) || 'Object'})`
        }
        seen.add(value)
    }

    if (isMap(value)) {
        const map = value as Map<unknown, unknown>
        const size = map.size
        if (size === 0) {
            return 'Map(0) {}'
        }
        const entries = [...map.entries()].slice(0, 2)
        const preview = entries.map(([k, v]) => `${getPreviewText(k, seen)} => ${getPreviewText(v, seen)}`).join(', ')
        const full = `Map(${size}) {${preview}${size > 2 ? ', ...' : ''}}`
        return full.length > MAX_PREVIEW_LEN ? full.slice(0, MAX_PREVIEW_LEN) + '…' : full
    }

    if (isSet(value)) {
        const set = value as Set<unknown>
        const size = set.size
        if (size === 0) {
            return 'Set(0) {}'
        }
        const vals = [...set.values()].slice(0, 3)
        const preview = vals.map((v) => getPreviewText(v, seen)).join(', ')
        const full = `Set(${size}) {${preview}${size > 3 ? ', ...' : ''}}`
        return full.length > MAX_PREVIEW_LEN ? full.slice(0, MAX_PREVIEW_LEN) + '…' : full
    }

    if (isArray(value)) {
        const len = value.length
        if (len === 0) {
            return '[]'
        }
        const preview = value
            .slice(0, 3)
            .map((v) => getPreviewText(v, seen))
            .join(', ')
        return `[${preview}${len > 3 ? ', ...' : ''}]`
    }

    if (isObject(value)) {
        const name = getObjectName(value)
        const keys = Object.keys(value as object)
        if (keys.length === 0) {
            return `${name} {}`.trim()
        }
        const preview = keys
            .slice(0, 2)
            .map((k) => `${k}: ${getPreviewText((value as Record<string, unknown>)[k], seen)}`)
            .join(', ')
        const full = `{${preview}${keys.length > 2 ? ', ...' : ''}}`
        return full.length > MAX_PREVIEW_LEN ? full.slice(0, MAX_PREVIEW_LEN) + '…' : full
    }

    return String(value)
}

/**
 * 로그 값 노드 생성
 */
const buildLogValueNode = (value: unknown, circularFinder = createCircularFinder(), depth = 0): LogValueNode => {
    const type = getLogValueType(value)
    const preview = getPreviewText(value)

    if (type !== 'object' && type !== 'array' && type !== 'map' && type !== 'set') {
        return {type, preview}
    }

    // 순환 참조
    if (circularFinder(value)) {
        return {
            type,
            preview: isArray(value) ? '(Circular Array)' : `(Circular ${getObjectName(value) || 'Object'})`,
        }
    }

    const children: LogValueEntry[] = []

    if (isMap(value)) {
        const map = value as Map<unknown, unknown>
        for (const [k, v] of map) {
            children.push({
                key: getPreviewText(k),
                value: buildLogValueNode(v, circularFinder, depth + 1),
            })
        }
        return {type, preview, children, objectName: 'Map', arrayLength: map.size}
    }

    if (isSet(value)) {
        const set = value as Set<unknown>
        let i = 0
        for (const v of set) {
            children.push({
                key: String(i++),
                value: buildLogValueNode(v, circularFinder, depth + 1),
            })
        }
        return {type, preview, children, objectName: 'Set', arrayLength: set.size}
    }

    if (isArray(value)) {
        const arr = value as unknown[]
        for (let i = 0; i < arr.length; i++) {
            children.push({
                key: String(i),
                value: buildLogValueNode(arr[i], circularFinder, depth + 1),
            })
        }
        return {type, preview, children, arrayLength: arr.length}
    }

    // object
    const obj = value as Record<string, unknown>

    // enumerable 키
    const enumKeys = Object.keys(obj)
    for (const key of enumKeys) {
        try {
            children.push({
                key,
                value: buildLogValueNode(obj[key], circularFinder, depth + 1),
            })
        } catch {
            children.push({key, value: {type: 'string', preview: '(PARSE_ERROR)'}})
        }
    }

    // non-enumerable 키
    const allKeys = Object.getOwnPropertyNames(obj)
    for (const key of allKeys) {
        if (enumKeys.includes(key)) {
            continue
        }
        try {
            children.push({
                key,
                value: buildLogValueNode(obj[key], circularFinder, depth + 1),
                nonEnumerable: true,
            })
        } catch {
            children.push({key, value: {type: 'string', preview: '(PARSE_ERROR)'}, nonEnumerable: true})
        }
    }

    // Symbol 키
    const symbolKeys = Object.getOwnPropertySymbols(obj)
    for (const sym of symbolKeys) {
        try {
            children.push({
                key: sym.toString(),
                value: buildLogValueNode((obj as Record<symbol, unknown>)[sym], circularFinder, depth + 1),
            })
        } catch {
            children.push({key: sym.toString(), value: {type: 'string', preview: '(PARSE_ERROR)'}})
        }
    }

    return {type, preview, children, objectName: getObjectName(value) || undefined}
}

export const buildLogValueNodes = (values: unknown[]): LogValueNode[] => {
    return values.map((value) => buildLogValueNode(value))
}

export const formatTimestamp = (timestamp: number): string => {
    const date = new Date(timestamp)
    const pad2 = (n: number) => String(n).padStart(2, '0')
    const pad3 = (n: number) => String(n).padStart(3, '0')
    return `${pad2(date.getHours())}:${pad2(date.getMinutes())}:${pad2(date.getSeconds())}.${pad3(date.getMilliseconds())}`
}
